import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FileSignature, PenLine, Plus, Trash2, Wand2 } from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useCompanyOptions } from '@/features/crm/api';
import { useProjects } from '@/features/delivery/api';
import { ApiRequestError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import {
  useDeleteDocumentTemplate,
  useDocumentTemplates,
  useGenerateDocument,
  useSaveDocumentTemplate,
  useSignatureRequests,
  useTemplatePreview,
} from './api';
import { CATEGORY_LABELS, SIGNATURE_STATUS_LABELS, SIGNATURE_STATUS_VARIANT } from './labels';
import type { DocumentTemplate } from './types';

const TABS = [
  ['templates', 'Templates'],
  ['signatures', 'Signature requests'],
] as const;

export function TemplatesPage() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'templates';

  return (
    <div>
      <PageHeader
        title="Templates and signatures"
        description="Generate an NDA or a statement of work from a record, then send it to be signed."
      />

      <div className="mb-5 flex gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'templates' ? <Templates /> : <Signatures />}
    </div>
  );
}

function Templates() {
  const { data: templates = [], isLoading } = useDocumentTemplates();
  const remove = useDeleteDocumentTemplate();
  const [editing, setEditing] = useState<DocumentTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState<DocumentTemplate | null>(null);

  if (isLoading) return <TableSkeleton columns={3} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="file.document.create">
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            New template
          </Button>
        </Can>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center">
            <FileSignature className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No templates yet. One holds the wording; the merge fields fill in the client.
            </p>
          </CardContent>
        </Card>
      ) : (
        templates.map((template) => (
          <Card key={template.id}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{template.name}</h3>
                    <Badge variant="secondary">{CATEGORY_LABELS[template.category]}</Badge>
                    {!template.isActive && <Badge variant="outline">off</Badge>}
                  </div>
                  {template.description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{template.description}</p>
                  )}
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Merges from a {template.entityType}
                    {template.mergeFields.length > 0 &&
                      ` · ${template.mergeFields.length} fields it will ask for`}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Can permission="file.document.create">
                    <Button size="sm" onClick={() => setGenerating(template)}>
                      <Wand2 className="h-4 w-4" />
                      Generate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(template)}>
                      Edit
                    </Button>
                  </Can>
                  <Can permission="file.document.delete">
                    <button
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => remove.mutate(template.id)}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </Can>
                </div>
              </div>

              {template.mergeFields.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {template.mergeFields.slice(0, 12).map((field) => (
                    <code key={field} className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                      {`{{${field}}}`}
                    </code>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {(creating || editing) && (
        <TemplateDialog
          template={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {generating && (
        <GenerateDialog template={generating} onClose={() => setGenerating(null)} />
      )}
    </div>
  );
}

function TemplateDialog({
  template,
  onClose,
}: {
  template?: DocumentTemplate;
  onClose: () => void;
}) {
  const save = useSaveDocumentTemplate();
  const [form, setForm] = useState({
    name: template?.name ?? '',
    description: template?.description ?? '',
    category: template?.category ?? 'OTHER',
    entityType: template?.entityType ?? 'company',
    body: template?.body ?? '<h1>Title</h1>\n<p>Dear {{client_name}},</p>\n',
  });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{template ? `Edit ${template.name}` : 'New template'}</DialogTitle>
          <DialogDescription>
            Write it as HTML with <code>{'{{merge_fields}}'}</code>. Give a field a fallback with
            a pipe: <code>{'{{city|Dubai}}'}</code>. A field with nothing to fill it stays visible
            in the output rather than leaving a gap nobody notices.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pe-1">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Merges from</Label>
              <Select
                value={form.entityType}
                onChange={(event) => setForm({ ...form, entityType: event.target.value })}
              >
                <option value="company">A client</option>
                <option value="project">A project</option>
                <option value="deal">A deal</option>
                <option value="contract">A contract</option>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value as never })}
              >
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Body</Label>
            <Textarea
              rows={16}
              className="font-mono text-xs"
              value={form.body}
              onChange={(event) => setForm({ ...form, body: event.target.value })}
            />
          </div>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending || form.name.trim().length < 2 || form.body.length < 10}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({ id: template?.id, ...form });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not save it.',
                );
              }
            }}
          >
            Save template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateDialog({
  template,
  onClose,
}: {
  template: DocumentTemplate;
  onClose: () => void;
}) {
  const generate = useGenerateDocument();
  const { data: companies = [] } = useCompanyOptions();
  const { data: projects } = useProjects({ limit: 100 });
  const [entityId, setEntityId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: preview } = useTemplatePreview(template.id, entityId || undefined);
  const options =
    template.entityType === 'project'
      ? (projects?.data ?? []).map((project) => ({
          id: project.id,
          label: `${project.code} — ${project.name}`,
        }))
      : companies.map((company) => ({ id: company.id, label: company.name }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Generate from {template.name}</DialogTitle>
          <DialogDescription>
            The result is a real document in the vault from the moment it is made — it versions,
            shares and signs like any other.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pe-1">
          <div className="space-y-1.5">
            <Label>
              {template.entityType === 'project' ? 'Project' : 'Client'}
            </Label>
            <Select value={entityId} onChange={(event) => setEntityId(event.target.value)}>
              <option value="">Choose one</option>
              {options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          {preview && (
            <>
              {preview.unresolved.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-medium">
                    {preview.unresolved.length} merge field
                    {preview.unresolved.length === 1 ? '' : 's'} did not resolve
                  </p>
                  <p className="mt-0.5 text-xs">
                    {preview.unresolved.map((field) => `{{${field}}}`).join(', ')} — they will stay
                    visible in the document so nobody sends it without noticing.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Preview</Label>
                <div
                  className="max-h-72 overflow-y-auto rounded-lg border bg-white p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              </div>
            </>
          )}

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!entityId || generate.isPending}
            onClick={async () => {
              setError(null);
              try {
                await generate.mutateAsync({
                  templateId: template.id,
                  entityId,
                  allowUnresolved: true,
                });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError ? caught.message : 'Could not generate it.',
                );
              }
            }}
          >
            {generate.isPending ? 'Generating…' : 'Generate document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Signatures() {
  const { data: requests = [], isLoading } = useSignatureRequests();

  if (isLoading) return <TableSkeleton columns={4} />;

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="py-14 text-center">
          <PenLine className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Nothing out for signature. Open a document and request one.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((request) => {
        const signed = request.signers.filter((signer) => signer.signedAt).length;

        return (
          <Card key={request.id}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{request.title}</h3>
                    <Badge variant={SIGNATURE_STATUS_VARIANT[request.status]}>
                      {SIGNATURE_STATUS_LABELS[request.status]}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    <Link
                      to={`/documents/${request.document.id}`}
                      className="text-link hover:underline"
                    >
                      {request.document.name}
                    </Link>
                    {request.sentAt && ` · sent ${formatDate(request.sentAt)}`}
                    {request.completedAt && ` · completed ${formatDate(request.completedAt)}`}
                  </p>
                </div>

                <div className="text-end text-sm">
                  <div className="tabular font-semibold">
                    {signed} of {request.signers.length} signed
                  </div>
                  <div className="mt-1 h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${(signed / Math.max(1, request.signers.length)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              <ul className="mt-3 space-y-1 border-t pt-3">
                {request.signers.map((signer) => (
                  <li key={signer.id} className="flex items-center justify-between text-sm">
                    <span>
                      {signer.name}
                      <span className="ms-2 text-xs text-muted-foreground">{signer.email}</span>
                    </span>
                    <span
                      className={
                        signer.signedAt
                          ? 'text-xs text-emerald-600'
                          : 'text-xs text-muted-foreground'
                      }
                    >
                      {signer.signedAt
                        ? `signed ${formatDate(signer.signedAt)}`
                        : signer.status.toLowerCase()}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
